//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

// A small, self-contained arithmetic expression evaluator for formula fields.
// Deliberately NOT using eval()/Function() — formula text comes from users and
// is evaluated server-side, so it goes through a real tokenizer/parser instead.
// Supports: + - * / ( ) unary minus, decimal numbers, and identifiers that
// resolve against a flat field-name -> value context.

export type FormulaContext = Record<string, number | string | boolean | undefined | null>

type TokenType = 'num' | 'ident' | 'op' | 'lparen' | 'rparen' | 'eof'

interface Token {
  type: TokenType
  value: string
}

export class FormulaError extends Error {}

function tokenize (expression: string): Token[] {
  const tokens: Token[] = []
  let i = 0
  while (i < expression.length) {
    const ch = expression[i]
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++
      continue
    }
    if (ch === '(') {
      tokens.push({ type: 'lparen', value: ch })
      i++
      continue
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen', value: ch })
      i++
      continue
    }
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/') {
      tokens.push({ type: 'op', value: ch })
      i++
      continue
    }
    if (/[0-9.]/.test(ch)) {
      let j = i
      while (j < expression.length && /[0-9.]/.test(expression[j])) j++
      tokens.push({ type: 'num', value: expression.slice(i, j) })
      i = j
      continue
    }
    if (/[A-Za-z_]/.test(ch)) {
      let j = i
      while (j < expression.length && /[A-Za-z0-9_]/.test(expression[j])) j++
      tokens.push({ type: 'ident', value: expression.slice(i, j) })
      i = j
      continue
    }
    throw new FormulaError(`Unexpected character "${ch}" in formula`)
  }
  tokens.push({ type: 'eof', value: '' })
  return tokens
}

class Parser {
  private pos = 0
  constructor (
    private readonly tokens: Token[],
    private readonly ctx: FormulaContext
  ) {}

  private peek (): Token {
    return this.tokens[this.pos]
  }

  private next (): Token {
    return this.tokens[this.pos++]
  }

  parse (): number {
    const value = this.parseExpr()
    if (this.peek().type !== 'eof') {
      throw new FormulaError(`Unexpected token "${this.peek().value}"`)
    }
    return value
  }

  private parseExpr (): number {
    let value = this.parseTerm()
    while (this.peek().type === 'op' && (this.peek().value === '+' || this.peek().value === '-')) {
      const op = this.next().value
      const rhs = this.parseTerm()
      value = op === '+' ? value + rhs : value - rhs
    }
    return value
  }

  private parseTerm (): number {
    let value = this.parseUnary()
    while (this.peek().type === 'op' && (this.peek().value === '*' || this.peek().value === '/')) {
      const op = this.next().value
      const rhs = this.parseUnary()
      if (op === '/') {
        if (rhs === 0) throw new FormulaError('Division by zero')
        value = value / rhs
      } else {
        value = value * rhs
      }
    }
    return value
  }

  private parseUnary (): number {
    if (this.peek().type === 'op' && this.peek().value === '-') {
      this.next()
      return -this.parseUnary()
    }
    return this.parsePrimary()
  }

  private parsePrimary (): number {
    const token = this.next()
    if (token.type === 'num') {
      return parseFloat(token.value)
    }
    if (token.type === 'ident') {
      return resolveIdentifier(token.value, this.ctx)
    }
    if (token.type === 'lparen') {
      const value = this.parseExpr()
      if (this.peek().type !== ('rparen' as TokenType)) throw new FormulaError('Expected ")"')
      this.next()
      return value
    }
    throw new FormulaError(`Unexpected token "${token.value}"`)
  }
}

function resolveIdentifier (name: string, ctx: FormulaContext): number {
  const raw = ctx[name]
  if (raw === undefined || raw === null) return 0
  if (typeof raw === 'number') return raw
  if (typeof raw === 'boolean') return raw ? 1 : 0
  const num = parseFloat(raw)
  return Number.isFinite(num) ? num : 0
}

// Throws FormulaError on invalid syntax; unknown/empty identifiers resolve to 0
// rather than throwing, so a formula stays valid while its inputs are still empty.
export function evaluateFormula (expression: string, ctx: FormulaContext): number {
  if (expression.trim().length === 0) {
    throw new FormulaError('Empty formula')
  }
  const tokens = tokenize(expression)
  return new Parser(tokens, ctx).parse()
}

// Returns the distinct field names an expression references, in first-seen order.
export function extractFormulaFieldRefs (expression: string): string[] {
  const tokens = tokenize(expression)
  const seen = new Set<string>()
  const result: string[] = []
  for (const token of tokens) {
    if (token.type === 'ident' && !seen.has(token.value)) {
      seen.add(token.value)
      result.push(token.value)
    }
  }
  return result
}

// Validates syntax only. Every referenced identifier is probed as 1 (not 0) so a
// syntactically valid "a / b" isn't rejected as a division-by-zero false positive.
export function validateFormulaSyntax (expression: string): string | undefined {
  try {
    const refs = extractFormulaFieldRefs(expression)
    const probeCtx: FormulaContext = {}
    for (const ref of refs) probeCtx[ref] = 1
    evaluateFormula(expression, probeCtx)
    return undefined
  } catch (err) {
    return err instanceof Error ? err.message : String(err)
  }
}
